import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { COLORS } from "assets";
import { alertTypeLabelKeyAt, MODEL_CHART_COLORS } from "config/constants";
import useTranslation from "hooks/useTranslation";
import { useMemo } from "react";
import Chart from "react-apexcharts";
import { ThreatModel } from "types";
import { getThresholdPercentForLevel } from "./thresholdStats";

const RING_SIZE = 68;

interface ICountChart {
  threats?: ThreatModel[];
  threatsOld?: ThreatModel[];
  loading?: boolean;
  period: string;
  level: number;
}

export default function CountChart({ loading, level, threats }: ICountChart) {
  const t = useTranslation();
  const color = useMemo(() => MODEL_CHART_COLORS[level] || COLORS.primary, [level]);

  const stats = useMemo(() => getThresholdPercentForLevel(threats, level), [threats, level]);

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: 112,
        height: "100%",
        bgcolor: COLORS.surface.card,
        borderRadius: `${COLORS.radius.lg}px`,
        boxShadow: COLORS.shadow.card,
        border: `1px solid ${COLORS.surface.border}`,
        px: 2.5,
        py: 2,
        display: "flex",
        alignItems: "center"
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1.5}>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            color: COLORS.surface.primaryText,
            fontWeight: 600,
            fontSize: 14,
            lineHeight: 1.35,
            letterSpacing: "-0.01em",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word"
          }}
        >
          {t[alertTypeLabelKeyAt(level)]}
        </Typography>

        <Box width={RING_SIZE} height={RING_SIZE} flexShrink={0} position="relative">
          {loading ? (
            <Box width={RING_SIZE} height={RING_SIZE} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={22} sx={{ color: COLORS.primary }} />
            </Box>
          ) : (
            <Chart
              width={RING_SIZE}
              height={RING_SIZE}
              series={[stats]}
              type="radialBar"
              options={{
                colors: [color],
                chart: {
                  sparkline: { enabled: true },
                  animations: { enabled: true, speed: 400 }
                },
                stroke: { lineCap: "round" },
                fill: { opacity: 1 },
                plotOptions: {
                  radialBar: {
                    startAngle: 0,
                    endAngle: 360,
                    hollow: {
                      size: "68%",
                      background: "transparent"
                    },
                    track: {
                      background: COLORS.surface.track,
                      strokeWidth: "100%",
                      margin: 0
                    },
                    dataLabels: {
                      show: true,
                      name: { show: false },
                      value: {
                        show: true,
                        fontSize: "12px",
                        fontWeight: 400,
                        fontFamily: "Roboto, sans-serif",
                        offsetY: 4,
                        color: COLORS.surface.secondaryText,
                        formatter: (val: number) => `${Math.round(val)}%`
                      }
                    }
                  }
                }
              }}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}
